import React from 'react';
import { useForm } from 'react-hook-form';
import { useEnquiryStore } from '../../store/useEnquiryStore';
import { CustomerDetails } from '../../types';

export const CustomerDetailsForm: React.FC = () => {
  const { currentEnquiry, updateCustomerDetails } = useEnquiryStore();
  const { register, handleSubmit, formState: { errors } } = useForm<CustomerDetails>({
    defaultValues: currentEnquiry.customerDetails
  });

  const onSubmit = (data: CustomerDetails) => {
    updateCustomerDetails(data);
  };

  const handleInputChange = (field: keyof CustomerDetails, value: string) => {
    updateCustomerDetails({ [field]: value });
  };

  return (
    <div className="p-4 lg:p-6">
      <div className="mb-6">
        <h2 className="text-xl lg:text-2xl font-semibold text-gray-900 mb-2">Customer Details</h2>
        <p className="text-sm lg:text-base text-gray-600">Please provide the customer's contact information and enquiry details.</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 lg:space-y-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
          <div>
            <label htmlFor="name" className="block text-sm lg:text-base font-medium text-gray-700 mb-2">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              {...register('name', { required: 'Name is required' })}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className="w-full px-4 py-3 lg:py-2 text-base lg:text-sm border border-gray-300 rounded-lg lg:rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter customer name"
            />
            {errors.name && <p className="mt-2 text-sm text-red-600">{errors.name.message}</p>}
          </div>

          <div>
            <label htmlFor="email" className="block text-sm lg:text-base font-medium text-gray-700 mb-2">
              Email
            </label>
            <input
              type="email"
              id="email"
              {...register('email', { 
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Invalid email format'
                }
              })}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className="w-full px-4 py-3 lg:py-2 text-base lg:text-sm border border-gray-300 rounded-lg lg:rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter email address"
            />
            {errors.email && <p className="mt-2 text-sm text-red-600">{errors.email.message}</p>}
          </div>
        </div>

        <div>
          <label htmlFor="address" className="block text-sm lg:text-base font-medium text-gray-700 mb-2">
            Address <span className="text-red-500">*</span>
          </label>
          <textarea
            id="address"
            {...register('address', { required: 'Address is required' })}
            onChange={(e) => handleInputChange('address', e.target.value)}
            rows={3}
            className="w-full px-4 py-3 lg:py-2 text-base lg:text-sm border border-gray-300 rounded-lg lg:rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
            placeholder="Enter customer address"
          />
          {errors.address && <p className="mt-2 text-sm text-red-600">{errors.address.message}</p>}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
          <div>
            <label htmlFor="phone" className="block text-sm lg:text-base font-medium text-gray-700 mb-2">
              Phone <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              id="phone"
              {...register('phone', { required: 'Phone number is required' })}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              className="w-full px-4 py-3 lg:py-2 text-base lg:text-sm border border-gray-300 rounded-lg lg:rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter phone number"
            />
            {errors.phone && <p className="mt-2 text-sm text-red-600">{errors.phone.message}</p>}
          </div>

          <div>
            <label htmlFor="mobile" className="block text-sm lg:text-base font-medium text-gray-700 mb-2">
              Mobile
            </label>
            <input
              type="tel"
              id="mobile"
              {...register('mobile')}
              onChange={(e) => handleInputChange('mobile', e.target.value)}
              className="w-full px-4 py-3 lg:py-2 text-base lg:text-sm border border-gray-300 rounded-lg lg:rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter mobile number"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
          <div>
            <label htmlFor="howHeard" className="block text-sm lg:text-base font-medium text-gray-700 mb-2">
              How did you hear about us?
            </label>
            <select
              id="howHeard"
              {...register('howHeard')}
              onChange={(e) => handleInputChange('howHeard', e.target.value)}
              className="w-full px-4 py-3 lg:py-2 text-base lg:text-sm border border-gray-300 rounded-lg lg:rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="">Select an option</option>
              <option value="Google">Google</option>
              <option value="Facebook">Facebook</option>
              <option value="Referral">Referral</option>
              <option value="Yellow Pages">Yellow Pages</option>
              <option value="Drive By">Drive By</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div>
            <label htmlFor="reasonForEnquiry" className="block text-sm lg:text-base font-medium text-gray-700 mb-2">
              Reason for Enquiry
            </label>
            <input
              type="text"
              id="reasonForEnquiry"
              {...register('reasonForEnquiry')}
              onChange={(e) => handleInputChange('reasonForEnquiry', e.target.value)}
              className="w-full px-4 py-3 lg:py-2 text-base lg:text-sm border border-gray-300 rounded-lg lg:rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., New home, renovation, repair"
            />
          </div>
        </div>

        <div>
          <label htmlFor="notes" className="block text-sm lg:text-base font-medium text-gray-700 mb-2">
            Additional Notes
          </label>
          <textarea
            id="notes"
            {...register('notes')}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            rows={4}
            className="w-full px-4 py-3 lg:py-2 text-base lg:text-sm border border-gray-300 rounded-lg lg:rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
            placeholder="Any additional notes or comments..."
          />
        </div>
      </form>
    </div>
  );
};